import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { api, errorMessage, fmtDate, fmtDay, getToken, jobTitle, setSession } from "@/lib/api";
import { initials } from "@/components/AppShell";
import { StatusBadge } from "@/components/Badges";
import { useMode } from "@/lib/mode";
import { useSeo } from "@/lib/seo";
import { LogOut, Save, ShieldCheck, Images, Sparkles, ArrowRight, Check } from "lucide-react";

export default function AccountPage({ user, onUser, onLogout }) {
  const { paths } = useMode();
  useSeo({ title: "Min konto", description: "Kontoen din hos MagicAd by DigiHome.", noindex: true });
  const [name, setName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const [usage, setUsage] = useState(null); // { ads, images, completed, latest }
  useEffect(() => {
    api.get("/v1/jobs", { params: { limit: 200 } }).then((r) => {
      const items = r.data.items || [];
      const images = items.reduce((a, j) => a + (j.images_count ?? j.images_total ?? 0), 0);
      const styled = items.reduce((a, j) => a + ((j.treatment_counts?.soft_style || 0) + (j.treatment_counts?.virtual_stage || 0)), 0);
      setUsage({ ads: r.data.total ?? items.length, images, styled, completed: items.filter((j) => j.status === "completed").length, latest: items.slice(0, 3) });
    }).catch(() => setUsage({ ads: 0, images: 0, styled: 0, completed: 0, latest: [] }));
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.patch("/auth/me", { name });
      setSession(getToken(), r.data.user);
      onUser?.(r.data.user);
      toast.success("Lagret");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const dirty = name.trim() !== (user?.name || "");
  return (
    <div className="mx-auto w-full max-w-[960px] px-5 py-10 sm:px-8 sm:py-14 space-y-8">
      <div className="flex items-start gap-5 rise-in">
        <span className="h-14 w-14 shrink-0 rounded-full bg-foreground text-background flex items-center justify-center text-[16px] font-medium">{initials(user?.name || user?.email)}</span>
        <div className="min-w-0">
          <div className="eyebrow">Min konto</div>
          <h1 className="display-sm mt-2 text-[36px] sm:text-[44px] truncate">Hei, {user?.name || user?.email}<span className="dot">.</span></h1>
          <p className="mt-2 text-dim">Kontoen din hos MagicAd by DigiHome.</p>
        </div>
      </div>

      {/* usage */}
      <section className="grid gap-px overflow-hidden rounded-[20px] ring-hair bg-foreground/[0.06] sm:grid-cols-3 rise-in rise-in-1" data-testid="account-usage">
        {usage === null ? [0, 1, 2].map((i) => <div key={i} className="bg-card p-5 sm:p-6"><Skeleton className="h-3 w-20" /><Skeleton className="mt-3 h-9 w-16" /></div>) : [
          ["Annonser", usage.ads, `${usage.completed} ferdige`],
          ["Bilder behandlet", usage.images, "originalene lagres uendret"],
          ["Visualiseringer", usage.styled, "stylet eller møblert – alltid merket"],
        ].map(([l, v, sub]) => (
          <div key={l} className="bg-card p-5 sm:p-6">
            <div className="eyebrow">{l}</div>
            <div className="font-heading text-[36px] metric-num mt-1.5 tracking-[-0.02em] leading-none" data-testid={`account-usage-${l.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div>
            <div className="mt-1.5 text-[12.5px] text-svak">{sub}</div>
          </div>
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
        <section className="panel p-6 sm:p-7 space-y-5 rise-in rise-in-2" data-testid="account-card">
          <h2 className="font-heading text-[22px] tracking-[-0.02em]">Profil</h2>
          <div className="grid gap-5 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="acc-name" className="text-[13.5px] text-foreground/80">Navn</Label>
              <Input id="acc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Navnet ditt" data-testid="account-name" />
            </div>
            <div className="space-y-2">
              <Label className="text-[13.5px] text-foreground/80">E-post</Label>
              <Input value={user?.email || ""} disabled data-testid="account-email" />
            </div>
          </div>
          <div className="text-[13px] text-svak">Konto opprettet {fmtDate(user?.created_at)} · sist innlogget {fmtDate(user?.last_login_at)}</div>
          <div className="flex flex-wrap gap-2 border-t hair pt-5">
            <Button onClick={save} disabled={busy || !dirty} data-testid="account-save">{busy ? <Check className="h-4 w-4" /> : <Save className="h-4 w-4" />}Lagre</Button>
            <Button variant="outline" onClick={onLogout} data-testid="account-logout"><LogOut className="h-4 w-4" />Logg ut</Button>
          </div>
        </section>

        <div className="space-y-6 rise-in rise-in-2">
          <section className="panel p-6 sm:p-7 space-y-3" data-testid="account-latest">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-[22px] tracking-[-0.02em]">Siste annonser</h2>
              <Link to={paths.jobs} className="text-[13.5px] font-medium inline-flex items-center gap-1 hover:underline underline-offset-4" data-testid="account-all-jobs">Alle<ArrowRight className="h-3.5 w-3.5" /></Link>
            </div>
            {usage === null ? <Skeleton className="h-16 rounded-[12px]" /> : usage.latest.length === 0 ? (
              <div className="rounded-[12px] bg-secondary/70 p-4 text-[14px] text-dim flex items-center gap-3"><Images className="h-4 w-4 shrink-0" />Ingen annonser enda. <Link to={paths.home} className="font-medium text-foreground hover:underline underline-offset-4" data-testid="account-first-job">Lag den første</Link></div>
            ) : usage.latest.map((j) => (
              <Link key={j.job_id} to={paths.job(j.job_id)} className="flex items-center justify-between gap-3 rounded-[12px] ring-hair px-3.5 py-2.5 hover:bg-secondary/60 transition-colors" data-testid={`account-job-${j.job_id}`}>
                <div className="min-w-0"><div className="text-[14.5px] font-medium truncate">{jobTitle(j)}</div><div className="text-[12.5px] text-dim">{fmtDay(j.created_at)}</div></div>
                <StatusBadge status={j.status} />
              </Link>
            ))}
            <Button variant="outline" size="sm" asChild className="w-full"><Link to={paths.home} data-testid="account-new-job"><Sparkles className="h-4 w-4" />Ny annonse</Link></Button>
          </section>

          <section className="rounded-[20px] surface-accent border p-6 sm:p-7">
            <div className="flex items-center gap-2 font-medium text-[15.5px]"><ShieldCheck className="h-4 w-4" />Slik behandler vi bildene dine</div>
            <p className="mt-2 text-[14px] leading-[1.55] opacity-90">Originalene lagres uendret og privat. Alle forbedrede versjoner kontrolleres mot originalen. Digitalt stylede og virtuelt møblerte bilder er alltid merket, kommer alltid etter det ekte bildet og publiseres aldri uten at du sier ja.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
